// Global Instructions Rule Applied!

import React, { useMemo } from 'react'
import { Table, Checkbox, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { AccountTopicsContact } from '../../../state_manager/data_models/account_topics_grid_model.ts'

const { Text } = Typography

// Shared, controlled presentational matrix: the account row + its
// comms-eligible contact rows down, topics across, checkboxes in the cells.
// Extracted from the Phase 2 Account Topic tab so the Phase 2 tab and each
// Phase 3 Bulk Topic Subscription Manager collapsible account panel render
// the IDENTICAL grid (plan §6: reuse, don't duplicate). No data fetching /
// dirty tracking here — the consumer owns selection state.

export interface MatrixTopic {
  comms_topics__id: string
  comms_topics__label: string
}

/** The contact fields the matrix reads, as the account-topics model validates them. */
export type MatrixContact = Pick<
  AccountTopicsContact,
  'contacts__id' | 'contacts__first_name' | 'contacts__last_name' | 'contacts__email'
>

const ACCOUNT_ROW_KEY = '__account__'

interface MatrixRow {
  key: string
  isAccount: boolean
  name: string
  email?: string | null
  contactId?: string
}

const contactName = (c: MatrixContact): string => {
  const full = [c.contacts__last_name, c.contacts__first_name].filter(Boolean).join(', ')
  return full || c.contacts__email || c.contacts__id
}

interface TopicMatrixProps {
  topics: MatrixTopic[]
  accountRowLabel: string
  contacts: MatrixContact[]
  /** selected topic ids for the account row */
  accountSel: Set<string>
  /** contactId -> selected topic ids */
  contactSel: Record<string, Set<string>>
  disabled?: boolean
  onToggleAccount: (topicId: string) => void
  onToggleContact: (contactId: string, topicId: string) => void
}

export function TopicMatrix({
  topics,
  accountRowLabel,
  contacts,
  accountSel,
  contactSel,
  disabled = false,
  onToggleAccount,
  onToggleContact
}: TopicMatrixProps): React.ReactElement {
  const rows: MatrixRow[] = useMemo(() => {
    const out: MatrixRow[] = [{ key: ACCOUNT_ROW_KEY, isAccount: true, name: accountRowLabel }]
    for (const c of contacts) {
      out.push({
        key: c.contacts__id,
        isAccount: false,
        name: contactName(c),
        email: c.contacts__email,
        contactId: c.contacts__id
      })
    }
    return out
  }, [contacts, accountRowLabel])

  const columns: ColumnsType<MatrixRow> = useMemo(() => {
    const nameCol: ColumnsType<MatrixRow>[number] = {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 260,
      render: (_: unknown, row: MatrixRow) =>
        row.isAccount ? (
          <Text strong>{row.name}</Text>
        ) : (
          <div className='flex flex-col'>
            <Text>{row.name}</Text>
            {row.email ? (
              <Text type='secondary' className='text-xs'>
                {row.email}
              </Text>
            ) : null}
          </div>
        )
    }

    const topicCols: ColumnsType<MatrixRow> = topics.map((t) => ({
      title: t.comms_topics__label,
      key: t.comms_topics__id,
      align: 'center' as const,
      width: 150,
      render: (_: unknown, row: MatrixRow) => {
        if (row.isAccount) {
          return (
            <Checkbox
              checked={accountSel.has(t.comms_topics__id)}
              disabled={disabled}
              onChange={() => onToggleAccount(t.comms_topics__id)}
            />
          )
        }
        const cid = row.contactId as string
        const accountOn = accountSel.has(t.comms_topics__id)
        return (
          <Checkbox
            checked={Boolean(contactSel[cid]?.has(t.comms_topics__id))}
            // A contact subscription only takes effect when the account is
            // subscribed to that topic (plan §1); gate the cell on it.
            disabled={!accountOn || disabled}
            onChange={() => onToggleContact(cid, t.comms_topics__id)}
          />
        )
      }
    }))

    return [nameCol, ...topicCols]
  }, [topics, accountSel, contactSel, disabled, onToggleAccount, onToggleContact])

  return (
    <Table<MatrixRow>
      size='small'
      columns={columns}
      dataSource={rows}
      pagination={false}
      sticky
      scroll={{ x: 'max-content' }}
      rowClassName={(row) => (row.isAccount ? 'topic-matrix-account-row' : '')}
    />
  )
}

export default TopicMatrix
